import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { useAppContext } from '../context/AppContext';
import { formatCurrency, formatDate } from '../utils/feeUtils';
import {
  Form,
  Button,
  Table,
  Spinner,
  Alert,
  Badge,
  Modal,
  Card,
  Row,
  Col,
  InputGroup,
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  FaCalendarCheck,
  FaSearch,
  FaPrint,
  FaFileExcel,
  FaChevronDown,
  FaChevronRight,
  FaExclamationTriangle,
  FaInfoCircle,
  FaCheckCircle,
  FaLayerGroup,
  FaCalendarAlt,
  FaClock,
  FaCheckSquare,
  FaSquare,
  FaMinusSquare,
  FaReceipt,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');
const STORAGE_KEY = 'batch_fee_due_preserved_state';

const loadPreservedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const savePreservedState = (stateObj) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateObj));
  } catch (e) {
    console.warn('Failed to save state to sessionStorage', e);
  }
};

const BatchFeeDue = () => {
  const navigate = useNavigate();
  const { selectedAcademicYear } = useAppContext();

  // Load preserved state from sessionStorage if available
  const savedState = useMemo(() => loadPreservedState(), []);
  const isMatchingAcademicYear =
    savedState &&
    String(savedState.academicYearId) === String(selectedAcademicYear?.id);

  // Filters state
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState(() => {
    if (isMatchingAcademicYear && savedState.selectedBatchId) {
      return savedState.selectedBatchId;
    }
    return '';
  });
  const [asOfDate, setAsOfDate] = useState(() => {
    if (isMatchingAcademicYear && savedState.asOfDate) {
      return savedState.asOfDate;
    }
    return new Date().toISOString().split('T')[0];
  });
  const [studentStatus, setStudentStatus] = useState(() => {
    if (isMatchingAcademicYear && savedState.studentStatus) {
      return savedState.studentStatus;
    }
    return 'Active';
  });
  const [searchTerm, setSearchTerm] = useState(() => {
    if (isMatchingAcademicYear && savedState.searchTerm) {
      return savedState.searchTerm;
    }
    return '';
  });

  // Data & loading state
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [batchData, setBatchData] = useState(() => {
    if (isMatchingAcademicYear && savedState.batchData) {
      return savedState.batchData;
    }
    return null;
  });
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Selection states
  const [selectedInstallmentIds, setSelectedInstallmentIds] = useState(() => {
    if (
      isMatchingAcademicYear &&
      Array.isArray(savedState?.selectedInstallmentIds)
    ) {
      return new Set(savedState.selectedInstallmentIds);
    }
    return new Set();
  });
  const [expandedStudentIds, setExpandedStudentIds] = useState(() => {
    if (
      isMatchingAcademicYear &&
      Array.isArray(savedState?.expandedStudentIds)
    ) {
      return new Set(savedState.expandedStudentIds);
    }
    return new Set();
  });

  // Modal states
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState('selected'); // 'selected' | 'batch'
  const [bulkDateMode, setBulkDateMode] = useState('due_date'); // 'due_date' | 'custom'
  const [bulkTransactionDate, setBulkTransactionDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Single action modal / state
  const [singleConfirmModal, setSingleConfirmModal] = useState({
    isOpen: false,
    type: 'student', // 'student' | 'installment'
    student: null,
    installment: null,
    processing: false,
    dateMode: 'due_date', // 'due_date' | 'custom'
    customDate: new Date().toISOString().split('T')[0],
  });

  // Automatically save state changes to sessionStorage
  useEffect(() => {
    if (selectedAcademicYear?.id) {
      savePreservedState({
        academicYearId: selectedAcademicYear.id,
        selectedBatchId,
        asOfDate,
        studentStatus,
        searchTerm,
        batchData,
        selectedInstallmentIds: Array.from(selectedInstallmentIds),
        expandedStudentIds: Array.from(expandedStudentIds),
      });
    }
  }, [
    selectedAcademicYear,
    selectedBatchId,
    asOfDate,
    studentStatus,
    searchTerm,
    batchData,
    selectedInstallmentIds,
    expandedStudentIds,
  ]);

  // Load batches when academic year changes
  useEffect(() => {
    const fetchBatches = async () => {
      const token = getAuthToken();
      if (!token) {
        setErrorMsg('Unauthorized! Please log in.');
        setBatches([]);
        return;
      }

      if (!selectedAcademicYear?.id) {
        setBatches([]);
        setSelectedBatchId('');
        return;
      }

      setLoadingBatches(true);
      setErrorMsg('');
      try {
        const res = await axios.get(
          `${appConstants.API_ROOT_URL}get-batches-v3`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { academic_year_id: selectedAcademicYear.id },
          },
        );

        if (res.data.status === 'success') {
          const list = res.data.data || [];
          setBatches(list);
          let targetBatchId = '';
          setSelectedBatchId((currentBatchId) => {
            if (
              currentBatchId &&
              list.some((b) => String(b.id) === String(currentBatchId))
            ) {
              targetBatchId = currentBatchId;
              return currentBatchId;
            }
            targetBatchId = list.length > 0 ? String(list[0].id) : '';
            return targetBatchId;
          });

          if (targetBatchId && !batchData) {
            fetchBatchPendingInstallments(targetBatchId);
          }
        } else {
          setErrorMsg(res.data.message || 'Failed to load batches.');
        }
      } catch (err) {
        setErrorMsg('Error loading batches.');
      } finally {
        setLoadingBatches(false);
      }
    };

    fetchBatches();
  }, [selectedAcademicYear]);

  // Fetch pending installments for selected batch
  const fetchBatchPendingInstallments = useCallback(
    async (batchIdToFetch = selectedBatchId) => {
      if (!batchIdToFetch) {
        setErrorMsg('Please select a batch.');
        return;
      }

      const token = getAuthToken();
      if (!token) {
        setErrorMsg('Unauthorized! Please log in.');
        navigate('/login');
        return;
      }

      setLoading(true);
      setErrorMsg('');
      setSuccessMsg('');
      setSelectedInstallmentIds(new Set());

      try {
        const payload = {
          batch_id: Number(batchIdToFetch),
          as_of_date: asOfDate,
          academic_year_id: selectedAcademicYear?.id,
          status: studentStatus === 'All' ? '' : studentStatus,
        };

        const res = await axios.post(
          `${appConstants.API_ROOT_URL}${appConstants.BATCH_FEE_DUE?.GET_PENDING_INSTALLMENTS || 'get_batch_pending_installments'}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (res.data.status === 'success') {
          setBatchData(res.data);
          // By default, expand all students so the user can easily see their installments
          const studentIds = (res.data.data || []).map((s) => s.student_id);
          setExpandedStudentIds(new Set(studentIds));
        } else {
          setErrorMsg(
            res.data.message || 'Failed to fetch batch pending installments.',
          );
          setBatchData(null);
        }
      } catch (err) {
        setErrorMsg(
          err.response?.data?.message ||
            'Error fetching batch pending installments data.',
        );
        setBatchData(null);
      } finally {
        setLoading(false);
      }
    },
    [selectedBatchId, asOfDate, selectedAcademicYear, studentStatus, navigate],
  );

  // Filter students based on client search
  const filteredStudents = useMemo(() => {
    if (!batchData?.data) return [];
    if (!searchTerm.trim()) return batchData.data;

    const term = searchTerm.toLowerCase().trim();
    return batchData.data.filter((student) => {
      const name = (student.student_name || '').toLowerCase();
      const admNo = (student.admission_no || '').toLowerCase();
      const matchInstallment = (student.installments || []).some(
        (inst) =>
          (inst.title || '').toLowerCase().includes(term) ||
          (inst.fee_structure_name || '').toLowerCase().includes(term),
      );
      return name.includes(term) || admNo.includes(term) || matchInstallment;
    });
  }, [batchData, searchTerm]);

  // All visible installment IDs
  const allVisibleInstallmentIds = useMemo(() => {
    const ids = [];
    filteredStudents.forEach((student) => {
      (student.installments || []).forEach((inst) => {
        const id = inst.student_fee_installment_id || inst.id;
        if (id) ids.push(id);
      });
    });
    return ids;
  }, [filteredStudents]);

  // Selection calculations
  const isAllSelected =
    allVisibleInstallmentIds.length > 0 &&
    allVisibleInstallmentIds.every((id) => selectedInstallmentIds.has(id));

  const isPartiallySelected =
    selectedInstallmentIds.size > 0 && !isAllSelected;

  // Selected totals
  const selectedStats = useMemo(() => {
    let totalAmt = 0;
    let totalInst = 0;
    const studentSet = new Set();

    if (!batchData?.data) {
      return { totalAmt, totalInst, studentCount: 0 };
    }

    batchData.data.forEach((student) => {
      (student.installments || []).forEach((inst) => {
        const id = inst.student_fee_installment_id || inst.id;
        if (selectedInstallmentIds.has(id)) {
          totalAmt += parseFloat(inst.amount || 0);
          totalInst += 1;
          studentSet.add(student.student_id);
        }
      });
    });

    return {
      totalAmt,
      totalInst,
      studentCount: studentSet.size,
    };
  }, [batchData, selectedInstallmentIds]);

  // Toggle selection for all visible installments
  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      // Deselect all
      setSelectedInstallmentIds(new Set());
    } else {
      // Select all visible
      setSelectedInstallmentIds(new Set(allVisibleInstallmentIds));
    }
  };

  // Toggle selection for a student
  const handleToggleSelectStudent = (student) => {
    const studentInstIds = (student.installments || []).map(
      (inst) => inst.student_fee_installment_id || inst.id,
    );
    const allStudentSelected = studentInstIds.every((id) =>
      selectedInstallmentIds.has(id),
    );

    const newSet = new Set(selectedInstallmentIds);
    if (allStudentSelected) {
      studentInstIds.forEach((id) => newSet.delete(id));
    } else {
      studentInstIds.forEach((id) => newSet.add(id));
    }
    setSelectedInstallmentIds(newSet);
  };

  // Toggle selection for an individual installment
  const handleToggleSelectInstallment = (installmentId) => {
    const newSet = new Set(selectedInstallmentIds);
    if (newSet.has(installmentId)) {
      newSet.delete(installmentId);
    } else {
      newSet.add(installmentId);
    }
    setSelectedInstallmentIds(newSet);
  };

  // Toggle expand/collapse student row
  const handleToggleExpandStudent = (studentId) => {
    const newSet = new Set(expandedStudentIds);
    if (newSet.has(studentId)) {
      newSet.delete(studentId);
    } else {
      newSet.add(studentId);
    }
    setExpandedStudentIds(newSet);
  };

  // Expand all / Collapse all
  const handleToggleExpandAll = () => {
    if (expandedStudentIds.size === filteredStudents.length) {
      setExpandedStudentIds(new Set());
    } else {
      const allIds = filteredStudents.map((s) => s.student_id);
      setExpandedStudentIds(new Set(allIds));
    }
  };

  // Open bulk make due modal
  const handleOpenBulkModal = (mode = 'selected') => {
    if (mode === 'selected' && selectedInstallmentIds.size === 0) {
      setErrorMsg('Please select at least one installment to make due.');
      return;
    }
    setBulkMode(mode);
    setBulkDateMode('due_date');
    setBulkTransactionDate(asOfDate || new Date().toISOString().split('T')[0]);
    setBulkModalOpen(true);
  };

  // Execute bulk make due
  const handleExecuteBulkMakeDue = async () => {
    const token = getAuthToken();
    if (!token) {
      setErrorMsg('Unauthorized! Please log in.');
      navigate('/login');
      return;
    }

    setBulkProcessing(true);
    setErrorMsg('');

    try {
      let payload = {};

      if (bulkDateMode === 'custom') {
        payload.date = bulkTransactionDate;
      } else {
        payload.use_installment_due_date = true;
        // If due date mode is chosen, check if all selected installments have the same due date
        if (bulkMode === 'selected') {
          const selectedDueDates = [];
          (batchData?.data || []).forEach((stu) => {
            (stu.installments || []).forEach((inst) => {
              const id = inst.student_fee_installment_id || inst.id;
              if (selectedInstallmentIds.has(id) && inst.due_date) {
                selectedDueDates.push(inst.due_date);
              }
            });
          });
          const uniqueDates = [...new Set(selectedDueDates)];
          if (uniqueDates.length === 1) {
            payload.date = uniqueDates[0];
          }
        }
      }

      if (bulkMode === 'selected') {
        payload.student_fee_installment_ids = Array.from(
          selectedInstallmentIds,
        );
      } else {
        payload.batch_id = Number(selectedBatchId);
        payload.academic_year_id = selectedAcademicYear?.id;
      }

      const res = await axios.post(
        `${appConstants.API_ROOT_URL}${appConstants.BATCH_FEE_DUE?.BULK_MAKE_DUE || 'bulk_make_installment_due'}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (res.data.status === 'success') {
        const msg =
          res.data.message ||
          `Successfully marked ${res.data.total_processed || 0} installment(s) as due (Total: ${formatCurrency(res.data.total_amount || 0)}).`;
        setSuccessMsg(msg);
        setBulkModalOpen(false);
        setSelectedInstallmentIds(new Set());
        // Re-fetch fresh data
        await fetchBatchPendingInstallments();
      } else {
        setErrorMsg(
          res.data.message || 'Failed to process bulk make installment due.',
        );
      }
    } catch (err) {
      setErrorMsg(
        err.response?.data?.message ||
          'An error occurred while making installments due.',
      );
    } finally {
      setBulkProcessing(false);
    }
  };

  // Single student / Single installment Make Due
  const handleOpenSingleConfirm = (type, student, installment = null) => {
    setSingleConfirmModal({
      isOpen: true,
      type,
      student,
      installment,
      processing: false,
      dateMode: 'due_date',
      customDate: asOfDate || new Date().toISOString().split('T')[0],
    });
  };

  const handleExecuteSingleMakeDue = async () => {
    const token = getAuthToken();
    if (!token) {
      setErrorMsg('Unauthorized! Please log in.');
      navigate('/login');
      return;
    }

    setSingleConfirmModal((prev) => ({ ...prev, processing: true }));
    setErrorMsg('');

    try {
      let installmentIds = [];
      let primaryDueDate = null;

      if (singleConfirmModal.type === 'student') {
        installmentIds = (
          singleConfirmModal.student?.installments || []
        ).map((i) => i.student_fee_installment_id || i.id);
        const dueDates = [
          ...new Set(
            (singleConfirmModal.student?.installments || [])
              .map((i) => i.due_date)
              .filter(Boolean),
          ),
        ];
        if (dueDates.length === 1) {
          primaryDueDate = dueDates[0];
        }
      } else if (singleConfirmModal.installment) {
        installmentIds = [
          singleConfirmModal.installment.student_fee_installment_id ||
            singleConfirmModal.installment.id,
        ];
        primaryDueDate = singleConfirmModal.installment.due_date;
      }

      const payload = {
        student_fee_installment_ids: installmentIds,
      };

      if (singleConfirmModal.dateMode === 'custom') {
        payload.date = singleConfirmModal.customDate;
      } else {
        payload.use_installment_due_date = true;
        if (primaryDueDate) {
          payload.date = primaryDueDate;
        }
      }

      const res = await axios.post(
        `${appConstants.API_ROOT_URL}${appConstants.BATCH_FEE_DUE?.BULK_MAKE_DUE || 'bulk_make_installment_due'}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (res.data.status === 'success') {
        setSuccessMsg(
          res.data.message ||
            `Successfully marked ${installmentIds.length} installment(s) as due.`,
        );
        setSingleConfirmModal((prev) => ({
          ...prev,
          isOpen: false,
          processing: false,
        }));
        await fetchBatchPendingInstallments();
      } else {
        setErrorMsg(
          res.data.message || 'Failed to process make installment due.',
        );
        setSingleConfirmModal((prev) => ({ ...prev, processing: false }));
      }
    } catch (err) {
      setErrorMsg(
        err.response?.data?.message ||
          'An error occurred while making installments due.',
      );
      setSingleConfirmModal((prev) => ({ ...prev, processing: false }));
    }
  };

  // Export to CSV / Excel
  const handleExportExcel = () => {
    if (!filteredStudents.length) return;

    const headers = [
      'Sl No',
      'Admission No',
      'Student Name',
      'Status',
      'Installment No',
      'Installment Title',
      'Fee Structure',
      'Due Date',
      'Amount (INR)',
      'Installment Status',
    ];

    const rows = [];
    let sl = 1;

    filteredStudents.forEach((student) => {
      (student.installments || []).forEach((inst) => {
        rows.push([
          sl++,
          student.admission_no || '',
          student.student_name || '',
          student.student_status || student.status || 'Active',
          inst.installment_no || '',
          inst.title || '',
          inst.fee_structure_name || '',
          inst.due_date || '',
          parseFloat(inst.amount || 0).toFixed(2),
          inst.status || 'pending',
        ]);
      });
    });

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => '"' + String(cell).replace(/"/g, '""') + '"')
          .join(','),
      )
      .join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const batchName = batchData?.batch_info
      ? `${batchData.batch_info.class_name}_${batchData.batch_info.division}`.replace(
          /\s+/g,
          '_',
        )
      : 'batch';
    link.href = url;
    link.download = `Batch_Pending_Installments_${batchName}_${asOfDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Print Handler
  const handlePrint = () => {
    window.print();
  };

  // Due date status badge helper
  const getDueDateBadge = (dueDateStr) => {
    if (!dueDateStr) return <Badge bg="secondary">No Due Date</Badge>;

    const todayStr = new Date().toISOString().split('T')[0];
    if (dueDateStr < todayStr) {
      return (
        <Badge bg="danger" className="text-uppercase" style={{ fontSize: '0.72rem' }}>
          Overdue ({formatDate(dueDateStr)})
        </Badge>
      );
    } else if (dueDateStr === todayStr) {
      return (
        <Badge bg="warning" text="dark" style={{ fontSize: '0.72rem' }}>
          Due Today
        </Badge>
      );
    } else {
      return (
        <Badge bg="info" text="dark" style={{ fontSize: '0.72rem' }}>
          Due on {formatDate(dueDateStr)}
        </Badge>
      );
    }
  };

  return (
    <Layout>
      {/* Styles for Topbar and Print Layout */}
      <style>{`
        .batch-fee-topbar {
          background: #1e293b;
          color: #f8fafc;
          border-radius: 10px;
          padding: 1rem 1.25rem;
          margin-bottom: 20px;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
        }
        .batch-fee-topbar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding-bottom: 10px;
        }
        .batch-fee-title {
          font-size: 1.25rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #60a5fa;
        }
        .batch-fee-controls {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .batch-fee-form-group {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 170px;
        }
        .batch-fee-form-group label {
          font-size: 0.85rem;
          font-weight: 600;
          color: #cbd5e1;
          white-space: nowrap;
          margin: 0;
        }
        .batch-fee-select, .batch-fee-input {
          background-color: #334155;
          border: 1px solid #475569;
          color: #ffffff;
          font-size: 0.875rem;
          border-radius: 6px;
        }
        .batch-fee-select:focus, .batch-fee-input:focus {
          background-color: #334155;
          color: #ffffff;
          border-color: #60a5fa;
          box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.25);
        }
        .batch-fee-summary-card {
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
          transition: transform 0.15s ease;
        }
        .batch-fee-table th {
          background-color: #f1f5f9 !important;
          color: #334155 !important;
          font-weight: 700 !important;
          font-size: 0.85rem !important;
          vertical-align: middle !important;
          border-color: #e2e8f0 !important;
        }
        .batch-fee-table td {
          vertical-align: middle !important;
          border-color: #f1f5f9 !important;
          font-size: 0.9rem;
        }
        .student-row-expanded {
          background-color: #f8fafc !important;
        }
        .installment-subtable {
          margin: 6px 0;
          border-radius: 6px;
          overflow: hidden;
          background-color: #ffffff;
          border: 1px solid #e2e8f0;
        }
        .installment-subtable th {
          background-color: #f8fafc !important;
          font-size: 0.78rem !important;
          font-weight: 600 !important;
          color: #64748b !important;
          padding: 6px 10px !important;
        }
        .installment-subtable td {
          font-size: 0.83rem !important;
          padding: 6px 10px !important;
        }
        @media print {
          @page {
            size: A4;
            margin: 15mm 10mm 15mm 10mm;
          }
          body * {
            visibility: hidden !important;
          }
          #batch-fee-print-area, #batch-fee-print-area * {
            visibility: visible !important;
          }
          #batch-fee-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }
          .no-print {
            display: none !important;
          }
          .batch-fee-table th, .batch-fee-table td {
            border: 1px solid #333 !important;
            padding: 4px 6px !important;
            font-size: 11px !important;
          }
        }
      `}</style>

      <div className="container-fluid py-3 px-md-4">
        {/* Topbar Filter Container */}
        <div className="batch-fee-topbar no-print">
          <div className="batch-fee-topbar-header">
            <div className="batch-fee-title">
              <FaCalendarCheck />
              <span>Batch Pending Fee Installments</span>
            </div>
            {batchData && (
              <div className="d-flex align-items-center gap-2">
                <Badge bg="primary" className="px-3 py-2 fs-6">
                  {batchData.batch_info?.class_name}{' '}
                  {batchData.batch_info?.division}
                </Badge>
                {selectedAcademicYear && (
                  <Badge bg="secondary" className="px-3 py-2">
                    {selectedAcademicYear.name}
                  </Badge>
                )}
              </div>
            )}
          </div>

          <div className="batch-fee-controls">
            {/* Batch Selector */}
            <div className="batch-fee-form-group">
              <label>
                <FaLayerGroup className="me-1" /> Batch:
              </label>
              <Form.Select
                size="sm"
                value={selectedBatchId}
                onChange={(e) => setSelectedBatchId(e.target.value)}
                className="batch-fee-select"
                disabled={loadingBatches || loading}
                style={{ minWidth: '160px' }}
              >
                <option value="">Select Batch</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.class_name} {b.division}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* As of Date (Cutoff Date) */}
            <div className="batch-fee-form-group">
              <label>
                <FaCalendarAlt className="me-1" /> As Of Date:
              </label>
              <Form.Control
                type="date"
                size="sm"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                className="batch-fee-input"
                disabled={loading}
                style={{ minWidth: '140px' }}
              />
            </div>

            {/* Student Status */}
            <div className="batch-fee-form-group">
              <label>Status:</label>
              <Form.Select
                size="sm"
                value={studentStatus}
                onChange={(e) => setStudentStatus(e.target.value)}
                className="batch-fee-select"
                disabled={loading}
                style={{ minWidth: '110px' }}
              >
                <option value="Active">Active</option>
                <option value="Suspend">Suspend</option>
                <option value="All">All</option>
              </Form.Select>
            </div>

            {/* Fetch Button */}
            <Button
              variant="primary"
              size="sm"
              onClick={() => fetchBatchPendingInstallments()}
              disabled={!selectedBatchId || loading}
              className="px-3 d-flex align-items-center gap-2"
              style={{ fontWeight: 600, height: '32px' }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" animation="border" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <FaSearch />
                  <span>Fetch Installments</span>
                </>
              )}
            </Button>

            {/* Action Buttons */}
            {batchData && batchData.data?.length > 0 && (
              <div className="ms-auto d-flex align-items-center gap-2 flex-wrap">
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={handleExportExcel}
                  className="d-flex align-items-center gap-1"
                  title="Export table data to CSV/Excel"
                >
                  <FaFileExcel className="text-success" />
                  <span>Excel</span>
                </Button>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={handlePrint}
                  className="d-flex align-items-center gap-1"
                  title="Print Report"
                >
                  <FaPrint />
                  <span>Print</span>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {errorMsg && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setErrorMsg('')}
            className="d-flex align-items-center gap-2 no-print"
          >
            <FaExclamationTriangle className="fs-5 flex-shrink-0" />
            <div>{errorMsg}</div>
          </Alert>
        )}

        {/* Success Alert */}
        {successMsg && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccessMsg('')}
            className="d-flex align-items-center gap-2 no-print"
          >
            <FaCheckCircle className="fs-5 flex-shrink-0" />
            <div>{successMsg}</div>
          </Alert>
        )}

        {/* Summary Statistics Cards */}
        {batchData && (
          <Row className="g-3 mb-3 no-print">
            <Col md={3} sm={6}>
              <Card className="batch-fee-summary-card p-3 border-start border-4 border-primary">
                <div className="text-muted small text-uppercase fw-bold">
                  Students with Pending Due
                </div>
                <div className="fs-4 fw-bold text-dark mt-1">
                  {batchData.total_students || batchData.data?.length || 0}
                </div>
                <div className="small text-muted mt-1">
                  Batch: {batchData.batch_info?.class_name}{' '}
                  {batchData.batch_info?.division}
                </div>
              </Card>
            </Col>

            <Col md={3} sm={6}>
              <Card className="batch-fee-summary-card p-3 border-start border-4 border-warning">
                <div className="text-muted small text-uppercase fw-bold">
                  Pending Installments Due
                </div>
                <div className="fs-4 fw-bold text-dark mt-1">
                  {batchData.total_installments || 0}
                </div>
                <div className="small text-muted mt-1">
                  Cutoff Date: {formatDate(batchData.as_of_date || asOfDate)}
                </div>
              </Card>
            </Col>

            <Col md={3} sm={6}>
              <Card className="batch-fee-summary-card p-3 border-start border-4 border-danger">
                <div className="text-muted small text-uppercase fw-bold">
                  Total Pending Amount
                </div>
                <div className="fs-4 fw-bold text-danger mt-1">
                  {formatCurrency(batchData.total_amount || 0)}
                </div>
                <div className="small text-muted mt-1">
                  Ready to be marked due
                </div>
              </Card>
            </Col>

            <Col md={3} sm={6}>
              <Card className="batch-fee-summary-card p-3 border-start border-4 border-success bg-light">
                <div className="text-muted small text-uppercase fw-bold">
                  Selected for Bulk Make Due
                </div>
                <div className="fs-4 fw-bold text-success mt-1">
                  {formatCurrency(selectedStats.totalAmt)}
                </div>
                <div className="small text-muted mt-1">
                  {selectedStats.totalInst} installment(s) across{' '}
                  {selectedStats.studentCount} student(s)
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Action Bar & Search Filter */}
        {batchData && batchData.data?.length > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 no-print bg-white p-2 px-3 rounded shadow-sm border">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Make Selected Due Button */}
              <Button
                variant="success"
                size="sm"
                onClick={() => handleOpenBulkModal('selected')}
                disabled={selectedInstallmentIds.size === 0 || loading}
                className="d-flex align-items-center gap-2 fw-semibold px-3"
              >
                <FaCheckSquare />
                <span>
                  Make Selected Due ({selectedInstallmentIds.size})
                </span>
                {selectedStats.totalAmt > 0 && (
                  <Badge bg="light" text="dark" className="ms-1">
                    {formatCurrency(selectedStats.totalAmt)}
                  </Badge>
                )}
              </Button>

              {/* Make Entire Batch Due Button */}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleOpenBulkModal('batch')}
                disabled={loading || !batchData.total_installments}
                className="d-flex align-items-center gap-1"
                title="Automatically marks all pending installments due on or before cutoff date for this batch"
              >
                <FaReceipt />
                <span>Make All Due (Entire Batch)</span>
              </Button>

              {/* Expand / Collapse All */}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleToggleExpandAll}
                className="d-flex align-items-center gap-1"
              >
                {expandedStudentIds.size === filteredStudents.length ? (
                  <>
                    <FaChevronDown /> Collapse All
                  </>
                ) : (
                  <>
                    <FaChevronRight /> Expand All
                  </>
                )}
              </Button>
            </div>

            {/* Search Input */}
            <div style={{ maxWidth: '280px', width: '100%' }}>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-light">
                  <FaSearch className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Filter student / ad.no..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setSearchTerm('')}
                  >
                    ✕
                  </Button>
                )}
              </InputGroup>
            </div>
          </div>
        )}

        {/* Data Table */}
        {batchData && batchData.data?.length > 0 ? (
          <div id="batch-fee-print-area">
            {/* Printable Header */}
            <div className="d-none d-print-block text-center mb-3">
              <h4>Batch Pending Fee Installments Due</h4>
              <p className="mb-1">
                <strong>Batch:</strong> {batchData.batch_info?.class_name}{' '}
                {batchData.batch_info?.division} | <strong>As Of Date:</strong>{' '}
                {formatDate(batchData.as_of_date || asOfDate)}
              </p>
              <p className="small text-muted">
                Total Students: {batchData.total_students} | Total
                Installments: {batchData.total_installments} | Total Amount: ₹
                {parseFloat(batchData.total_amount || 0).toFixed(2)}
              </p>
            </div>

            <div className="card shadow-sm border-0 overflow-hidden mb-4">
              <div className="table-responsive">
                <Table hover className="batch-fee-table mb-0 align-middle">
                  <thead>
                    <tr>
                      <th
                        style={{ width: '40px' }}
                        className="text-center no-print"
                      >
                        <div
                          style={{ cursor: 'pointer' }}
                          onClick={handleToggleSelectAll}
                          title="Select / Deselect All"
                        >
                          {isAllSelected ? (
                            <FaCheckSquare className="text-primary fs-5" />
                          ) : isPartiallySelected ? (
                            <FaMinusSquare className="text-primary fs-5" />
                          ) : (
                            <FaSquare className="text-muted fs-5" />
                          )}
                        </div>
                      </th>
                      <th style={{ width: '50px' }} className="text-center">
                        #
                      </th>
                      <th style={{ width: '130px' }}>Admission No</th>
                      <th>Student Name</th>
                      <th style={{ width: '100px' }} className="text-center">
                        Status
                      </th>
                      <th style={{ width: '140px' }} className="text-center">
                        Pending Installments
                      </th>
                      <th style={{ width: '150px' }} className="text-end">
                        Pending Amount
                      </th>
                      <th
                        style={{ width: '150px' }}
                        className="text-center no-print"
                      >
                        Quick Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student, idx) => {
                      const studentInstIds = (
                        student.installments || []
                      ).map(
                        (inst) => inst.student_fee_installment_id || inst.id,
                      );
                      const isStudentSelected =
                        studentInstIds.length > 0 &&
                        studentInstIds.every((id) =>
                          selectedInstallmentIds.has(id),
                        );
                      const isStudentPartial =
                        studentInstIds.some((id) =>
                          selectedInstallmentIds.has(id),
                        ) && !isStudentSelected;
                      const isExpanded = expandedStudentIds.has(
                        student.student_id,
                      );

                      return (
                        <React.Fragment key={student.student_id}>
                          {/* Student Main Row */}
                          <tr
                            className={
                              isExpanded ? 'student-row-expanded' : ''
                            }
                            style={{
                              borderBottom: isExpanded
                                ? 'none'
                                : '1px solid #f1f5f9',
                            }}
                          >
                            {/* Student Checkbox */}
                            <td className="text-center no-print">
                              <div
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                  handleToggleSelectStudent(student)
                                }
                              >
                                {isStudentSelected ? (
                                  <FaCheckSquare className="text-primary fs-5" />
                                ) : isStudentPartial ? (
                                  <FaMinusSquare className="text-primary fs-5" />
                                ) : (
                                  <FaSquare className="text-muted fs-5" />
                                )}
                              </div>
                            </td>

                            <td className="text-center text-muted fw-bold">
                              {idx + 1}
                            </td>

                            <td>
                              <span className="badge bg-light text-dark border px-2 py-1 font-monospace">
                                {student.admission_no || '-'}
                              </span>
                            </td>

                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <button
                                  className="btn btn-link btn-sm p-0 text-decoration-none text-dark d-flex align-items-center gap-1 fw-bold text-start no-print"
                                  onClick={() =>
                                    handleToggleExpandStudent(
                                      student.student_id,
                                    )
                                  }
                                >
                                  {isExpanded ? (
                                    <FaChevronDown className="text-muted small" />
                                  ) : (
                                    <FaChevronRight className="text-muted small" />
                                  )}
                                  <span>{student.student_name}</span>
                                </button>
                                <span className="d-none d-print-inline fw-bold">
                                  {student.student_name}
                                </span>

                                <Link
                                  to={`/new-fee-management/${student.student_id}`}
                                  className="badge bg-light text-primary border text-decoration-none no-print"
                                  title="Open Student Fee Management"
                                >
                                  Fee Sheet ↗
                                </Link>
                              </div>
                            </td>

                            <td className="text-center">
                              <Badge
                                bg={
                                  (student.student_status ||
                                    student.status) === 'Active'
                                    ? 'success'
                                    : 'secondary'
                                }
                                style={{ fontSize: '0.75rem' }}
                              >
                                {student.student_status ||
                                  student.status ||
                                  'Active'}
                              </Badge>
                            </td>

                            <td className="text-center">
                              <Badge
                                bg="warning"
                                text="dark"
                                className="px-2 py-1"
                              >
                                {student.installments_count ||
                                  student.installments?.length ||
                                  0}{' '}
                                installment(s)
                              </Badge>
                            </td>

                            <td className="text-end fw-bold text-danger">
                              {formatCurrency(
                                student.total_pending_amount || 0,
                              )}
                            </td>

                            <td className="text-center no-print">
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="py-1 px-2 text-nowrap"
                                style={{ fontSize: '0.8rem' }}
                                onClick={() =>
                                  handleOpenSingleConfirm('student', student)
                                }
                              >
                                <FaCheckCircle className="me-1" />
                                Make Due
                              </Button>
                            </td>
                          </tr>

                          {/* Expanded Installments Row */}
                          {isExpanded && (
                            <tr className="student-row-expanded">
                              <td colSpan="8" className="p-2 pt-0 pb-3 ps-5">
                                <div className="installment-subtable shadow-sm">
                                  <Table
                                    size="sm"
                                    className="mb-0 align-middle"
                                  >
                                    <thead>
                                      <tr>
                                        <th
                                          style={{ width: '40px' }}
                                          className="text-center no-print"
                                        >
                                          Select
                                        </th>
                                        <th style={{ width: '60px' }}>#</th>
                                        <th>Installment Title</th>
                                        <th>Fee Structure</th>
                                        <th style={{ width: '160px' }}>
                                          Due Date
                                        </th>
                                        <th
                                          style={{ width: '130px' }}
                                          className="text-end"
                                        >
                                          Amount
                                        </th>
                                        <th
                                          style={{ width: '100px' }}
                                          className="text-center"
                                        >
                                          Status
                                        </th>
                                        <th
                                          style={{ width: '110px' }}
                                          className="text-center no-print"
                                        >
                                          Action
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(student.installments || []).map(
                                        (inst, iIdx) => {
                                          const instId =
                                            inst.student_fee_installment_id ||
                                            inst.id;
                                          const isSelected =
                                            selectedInstallmentIds.has(instId);

                                          return (
                                            <tr
                                              key={instId || iIdx}
                                              className={
                                                isSelected
                                                  ? 'table-warning'
                                                  : ''
                                              }
                                            >
                                              <td className="text-center no-print">
                                                <div
                                                  style={{ cursor: 'pointer' }}
                                                  onClick={() =>
                                                    handleToggleSelectInstallment(
                                                      instId,
                                                    )
                                                  }
                                                >
                                                  {isSelected ? (
                                                    <FaCheckSquare className="text-primary" />
                                                  ) : (
                                                    <FaSquare className="text-muted" />
                                                  )}
                                                </div>
                                              </td>
                                              <td className="text-muted font-monospace">
                                                #{inst.installment_no || iIdx + 1}
                                              </td>
                                              <td className="fw-semibold text-dark">
                                                {inst.title ||
                                                  `Installment ${inst.installment_no}`}
                                              </td>
                                              <td className="text-muted">
                                                {inst.fee_structure_name || '-'}
                                              </td>
                                              <td>
                                                {getDueDateBadge(inst.due_date)}
                                              </td>
                                              <td className="text-end fw-bold text-danger">
                                                {formatCurrency(inst.amount)}
                                              </td>
                                              <td className="text-center">
                                                <Badge
                                                  bg="secondary"
                                                  style={{ fontSize: '0.72rem' }}
                                                >
                                                  {inst.status || 'pending'}
                                                </Badge>
                                              </td>
                                              <td className="text-center no-print">
                                                <Button
                                                  variant="outline-primary"
                                                  size="sm"
                                                  className="py-0 px-2"
                                                  style={{
                                                    fontSize: '0.75rem',
                                                  }}
                                                  onClick={() =>
                                                    handleOpenSingleConfirm(
                                                      'installment',
                                                      student,
                                                      inst,
                                                    )
                                                  }
                                                >
                                                  Make Due
                                                </Button>
                                              </td>
                                            </tr>
                                          );
                                        },
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        ) : batchData && batchData.data?.length === 0 ? (
          <Card className="text-center p-5 border-0 shadow-sm bg-white">
            <div className="py-4">
              <FaCheckCircle className="text-success display-3 mb-3" />
              <h4>No Pending Installments Due!</h4>
              <p className="text-muted max-w-md mx-auto">
                All fee installments for batch{' '}
                <strong>
                  {batchData.batch_info?.class_name}{' '}
                  {batchData.batch_info?.division}
                </strong>{' '}
                due on or before {formatDate(batchData.as_of_date || asOfDate)}{' '}
                are already marked due or fully up to date.
              </p>
            </div>
          </Card>
        ) : !loading && (
          <Card className="text-center p-5 border-0 shadow-sm bg-white">
            <div className="py-4">
              <FaClock className="text-primary display-4 mb-3" />
              <h4>Select a Batch & Cutoff Date</h4>
              <p className="text-muted max-w-md mx-auto">
                Select a batch and due cutoff date in the toolbar above, then click{' '}
                <strong>Fetch Installments</strong> to inspect and mark pending
                fee installments as due in bulk.
              </p>
            </div>
          </Card>
        )}

        {/* Loading Overlay State */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2 text-muted fw-semibold">
              Loading batch pending fee installments...
            </div>
          </div>
        )}
      </div>

      {/* Bulk Make Due Modal */}
      <Modal
        show={bulkModalOpen}
        onHide={() => !bulkProcessing && setBulkModalOpen(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!bulkProcessing} className="bg-light">
          <Modal.Title className="fs-5 d-flex align-items-center gap-2">
            <FaCheckCircle className="text-success" />
            <span>Confirm Bulk Make Installments Due</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <p className="text-muted mb-3">
              {bulkMode === 'selected' ? (
                <>
                  You are about to mark{' '}
                  <strong className="text-dark">
                    {selectedInstallmentIds.size} installment(s)
                  </strong>{' '}
                  for{' '}
                  <strong className="text-dark">
                    {selectedStats.studentCount} student(s)
                  </strong>{' '}
                  as due.
                </>
              ) : (
                <>
                  You are about to mark{' '}
                  <strong>all pending installments</strong> due on or before{' '}
                  <strong>{formatDate(asOfDate)}</strong> for batch{' '}
                  <strong>
                    {batchData?.batch_info?.class_name}{' '}
                    {batchData?.batch_info?.division}
                  </strong>{' '}
                  as due.
                </>
              )}
            </p>

            <div className="bg-light p-3 rounded border mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Batch:</span>
                <span className="fw-semibold">
                  {batchData?.batch_info?.class_name}{' '}
                  {batchData?.batch_info?.division}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Total Installments:</span>
                <span className="fw-semibold">
                  {bulkMode === 'selected'
                    ? selectedInstallmentIds.size
                    : batchData?.total_installments || 0}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Total Amount:</span>
                <span className="fw-bold text-danger fs-6">
                  {bulkMode === 'selected'
                    ? formatCurrency(selectedStats.totalAmt)
                    : formatCurrency(batchData?.total_amount || 0)}
                </span>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small d-block mb-2">
                Transaction Date for Accounting Entries:
              </Form.Label>
              <div className="d-flex flex-column gap-2 p-3 bg-light rounded border">
                <Form.Check
                  type="radio"
                  id="bulk-date-due-date"
                  name="bulkDateOption"
                  label={
                    <span>
                      <strong className="text-dark">
                        Use scheduled due date
                      </strong>{' '}
                      <span className="text-muted small">
                        (Each installment transaction will be dated on its respective scheduled due date)
                      </span>
                    </span>
                  }
                  checked={bulkDateMode === 'due_date'}
                  onChange={() => setBulkDateMode('due_date')}
                  disabled={bulkProcessing}
                />
                <Form.Check
                  type="radio"
                  id="bulk-date-custom"
                  name="bulkDateOption"
                  label={
                    <span>
                      <strong className="text-dark">
                        Use specific transaction date
                      </strong>
                    </span>
                  }
                  checked={bulkDateMode === 'custom'}
                  onChange={() => setBulkDateMode('custom')}
                  disabled={bulkProcessing}
                />
                {bulkDateMode === 'custom' && (
                  <div className="ps-4 pt-1">
                    <Form.Control
                      type="date"
                      size="sm"
                      value={bulkTransactionDate}
                      onChange={(e) => setBulkTransactionDate(e.target.value)}
                      disabled={bulkProcessing}
                      style={{ maxWidth: '220px' }}
                    />
                  </div>
                )}
              </div>
              <Form.Text className="text-muted small mt-1 d-block">
                Double-entry transactions (Student Debit & Fee Receivable
                Credit) will be posted on the selected date.
              </Form.Text>
            </Form.Group>

            <Alert variant="info" className="py-2 px-3 small mb-0">
              <FaInfoCircle className="me-1" />
              This action is atomic and will update the status of each
              installment from <code>pending</code> to <code>due</code>.
            </Alert>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setBulkModalOpen(false)}
            disabled={bulkProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleExecuteBulkMakeDue}
            disabled={bulkProcessing}
            className="d-flex align-items-center gap-2"
          >
            {bulkProcessing ? (
              <>
                <Spinner size="sm" animation="border" />
                <span>Processing Bulk Due...</span>
              </>
            ) : (
              <>
                <FaCheckCircle />
                <span>Confirm & Make Due</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Single / Student Make Due Modal */}
      <Modal
        show={singleConfirmModal.isOpen}
        onHide={() =>
          !singleConfirmModal.processing &&
          setSingleConfirmModal((prev) => ({ ...prev, isOpen: false }))
        }
        centered
      >
        <Modal.Header
          closeButton={!singleConfirmModal.processing}
          className="bg-light"
        >
          <Modal.Title className="fs-5 d-flex align-items-center gap-2">
            <FaCheckCircle className="text-success" />
            <span>
              {singleConfirmModal.type === 'student'
                ? 'Make Due for Student'
                : 'Make Installment Due'}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            {singleConfirmModal.type === 'student' ? (
              <>
                Mark all{' '}
                <strong>
                  {singleConfirmModal.student?.installments?.length || 0}
                </strong>{' '}
                pending installments for{' '}
                <strong>{singleConfirmModal.student?.student_name}</strong> (Ad.
                No: {singleConfirmModal.student?.admission_no}) as due?
              </>
            ) : (
              <>
                Mark installment{' '}
                <strong>
                  #{singleConfirmModal.installment?.installment_no} (
                  {singleConfirmModal.installment?.title})
                </strong>{' '}
                amounting to{' '}
                <strong>
                  {formatCurrency(singleConfirmModal.installment?.amount)}
                </strong>{' '}
                for student{' '}
                <strong>{singleConfirmModal.student?.student_name}</strong> as
                due?
              </>
            )}
          </p>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold small d-block mb-2">
              Transaction Date:
            </Form.Label>
            <div className="d-flex flex-column gap-2 p-3 bg-light rounded border">
              <Form.Check
                type="radio"
                id="single-date-due-date"
                name="singleDateOption"
                label={
                  <span>
                    <strong className="text-dark">
                      {singleConfirmModal.installment?.due_date
                        ? `Use installment scheduled due date (${formatDate(singleConfirmModal.installment.due_date)})`
                        : 'Use scheduled due date'}
                    </strong>
                  </span>
                }
                checked={singleConfirmModal.dateMode === 'due_date'}
                onChange={() =>
                  setSingleConfirmModal((prev) => ({
                    ...prev,
                    dateMode: 'due_date',
                  }))
                }
                disabled={singleConfirmModal.processing}
              />
              <Form.Check
                type="radio"
                id="single-date-custom"
                name="singleDateOption"
                label={
                  <span>
                    <strong className="text-dark">
                      Use specific transaction date
                    </strong>
                  </span>
                }
                checked={singleConfirmModal.dateMode === 'custom'}
                onChange={() =>
                  setSingleConfirmModal((prev) => ({
                    ...prev,
                    dateMode: 'custom',
                  }))
                }
                disabled={singleConfirmModal.processing}
              />
              {singleConfirmModal.dateMode === 'custom' && (
                <div className="ps-4 pt-1">
                  <Form.Control
                    type="date"
                    size="sm"
                    value={singleConfirmModal.customDate}
                    onChange={(e) =>
                      setSingleConfirmModal((prev) => ({
                        ...prev,
                        customDate: e.target.value,
                      }))
                    }
                    disabled={singleConfirmModal.processing}
                    style={{ maxWidth: '220px' }}
                  />
                </div>
              )}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setSingleConfirmModal((prev) => ({ ...prev, isOpen: false }))
            }
            disabled={singleConfirmModal.processing}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleExecuteSingleMakeDue}
            disabled={singleConfirmModal.processing}
            className="d-flex align-items-center gap-2"
          >
            {singleConfirmModal.processing ? (
              <>
                <Spinner size="sm" animation="border" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <FaCheckCircle />
                <span>Make Due</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default BatchFeeDue;
